import { computed, ref } from 'vue';
import {
  catalogColumns,
  catalogRowMode,
  catalogTier,
  catalogWidthStep,
  TCatalogWidthStep,
} from '@/domain/catalog/catalog-layout';
import type { ICanvasPageSize } from '@/components/v2/layout/canvas-page';
import { useAssistantStore } from '@/composition/assistant/use-assistant-store';
import { useStableWindowSize } from '@/composition/use-stable-window-size';
import { useFocusSafeTier } from './use-focus-safe-tier';

/** The drawer is permanent (not an overlay) from Vuetify's desktop widths. */
const PERMANENT_DRAWER_FROM = 1264;
const DRAWER_WIDTH = 256;
/** `V2ShellTopBar`, above every page. */
const TOP_BAR_HEIGHT = 48;

/**
 * The page width before the canvas measures it (avoids a first phone render),
 * from the window width the app already tracks and the docked assistant's
 * inset: reading the document's width here would force a layout while the
 * page is still being built.
 */
export function estimatedPageWidth(
  windowWidth: number,
  insetRight: number
): number {
  const drawer = windowWidth >= PERMANENT_DRAWER_FROM ? DRAWER_WIDTH : 0;
  return Math.max(0, windowWidth - drawer - insetRight);
}

/**
 * The Menú page's layout (plan §5.2): the tier and columns from the page's
 * own width, and the row mode from the list column's width.
 */
export function useCatalogLayout() {
  const { width: windowWidth, height: windowHeight } = useStableWindowSize();
  const assistant = useAssistantStore();
  const pageWidth = ref(
    estimatedPageWidth(windowWidth.value, assistant.insetRight)
  );
  // Estimated until the canvas measures it, so the first render already
  // mounts the eager cards (only windowing reads it; tiers never do).
  const viewHeight = ref(Math.max(0, windowHeight.value - TOP_BAR_HEIGHT));
  const headHeight = ref(0);
  /** Measured by the list (one observer on its container, never per row). */
  const listWidth = ref(0);

  const { tier } = useFocusSafeTier(
    computed(() => catalogTier(pageWidth.value))
  );
  const widthStep = computed<TCatalogWidthStep>(() =>
    tier.value === 'desktop' ? catalogWidthStep(pageWidth.value) : 0
  );
  const columns = computed(() =>
    catalogColumns(tier.value, widthStep.value, {
      assistantDocked: assistant.insetRight > 0,
    })
  );
  const rowMode = computed(() =>
    listWidth.value ? catalogRowMode(listWidth.value) : 'compact'
  );

  function onCanvasResize(size: ICanvasPageSize) {
    pageWidth.value = size.width;
    viewHeight.value = size.viewHeight;
    headHeight.value = size.headHeight;
  }

  function setListWidth(width: number) {
    listWidth.value = width;
  }

  return {
    pageWidth,
    viewHeight,
    headHeight,
    tier,
    widthStep,
    columns,
    rowMode,
    onCanvasResize,
    setListWidth,
  };
}

export type ICatalogLayout = ReturnType<typeof useCatalogLayout>;
